import {
	Card,
	CardContent,
	IconButton,
	List,
	ListItem,
	ListItemButton,
	ListItemText,
	Stack,
	Table,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Tooltip,
} from "@mui/material";
import { useTemplatesStore } from "../../store/Templates/templates.store";
import { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import DeleteIcon from "@mui/icons-material/Delete";
import { TableBody } from "@mui/material";
import { PlusSquare, Wand2, Copy } from "lucide-react";
import { EditableTitle } from "@/components/src/editable-title/editable-title.component";
import { displayTemplateLabel } from "../../store/Templates/utils";
import { useCentralStore } from "../../store/Central";
import { useRefContext } from "../../context/RefContext";
import { Toolbar } from "../job-page/job-document/job-document-components/job-items-table/job-items-table.component";
import { useAutoSave } from "../../hooks/useAutosave/useAutosave";
import { CreateNewTemplateDialog } from "./job-document-template-dialoges/create-new-template.dialog";
import { showNotification } from "../../store/Central/selectors";
import { JobItemEntityType } from "@/lib/supabase/supabaseTypes";
import { Button } from "../../components/ui/button";
import { StandardTemplatesDialog } from "./standard-templates-dialog.component";
import { standardJobDocumentTemplates } from "./standard-job-document-templates";
import { useJobStore } from "../../store/Jobs";
import { useCatalogsStore } from "../../store/Catalogs";
import { useClipboard } from "../../context/ClipboardContext";
import { getTemplateItemDetails } from "../../store/Templates/template-item-details";
import { TemplateTotals } from "./template-totals.component";
import { PriceListSelect } from "../../forms/form-components/price-list-select.component";
import { REGION_METADATA } from "../../types/region.types";
import { RoundToType } from "../../store/Jobs/utils";
import { useServiceCatalogStore } from "../../store/ServiceCatalog";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "../../components/ui/alert-dialog";

export const JobDocumentTemplatesPage = () => {
	const { container } = useRefContext();
	const [searchParams, setSearchParams] = useSearchParams();
	const templateId = searchParams.get("templateId");
	const [searchQuery, setSearchQuery] = useState("");
	const [createNewTemplateDialogOpen, setCreateNewTemplateDialogOpen] =
		useState(false);
	const [
		createFromStandardTemplateDialogOpen,
		setCreateFromStandardTemplateDialogOpen,
	] = useState(false);
	const [deleteDialogTemplateId, setDeleteDialogTemplateId] = useState<
		number | null
	>(null);
	const [deleteCatalogFolders, setDeleteCatalogFolders] = useState<string[]>(
		[]
	);

	const { openedSidebar } = useJobStore((state) => ({
		openedSidebar: state.openedSidebar,
	}));

	const { templatesLookup, deleteTemplate, createTemplate } =
		useTemplatesStore((state) => ({
			templatesLookup: state.templatesLookup,
			deleteTemplate: state.deleteTemplate,
			createTemplate: state.createTemplate,
		}));

	const { organization, priceLists, articles, activatedRegions } =
		useCentralStore((state) => ({
			organization: state.organization,
			priceLists: state.priceLists,
			articles: state.articles,
			activatedRegions: state.activatedRegions,
		}));

	const { checkItemUsage } = useServiceCatalogStore((state) => ({
		checkItemUsage: state.checkItemUsage,
	}));

	const { tariffsLookup } = useCatalogsStore((state) => ({
		tariffsLookup: state.tariffsLookup,
	}));

	const defaultPriceListId = organization?.default_price_list_id ?? null;
	const defaultRegionId = organization?.default_activated_region_id ?? null;
	const defaultPriceList = priceLists.find(
		(pl) => pl.id === defaultPriceListId
	);

	const [selectedPriceListIdState, setSelectedPriceListId] = useState<
		string | null
	>(defaultPriceListId);
	const [selectedRegionIdState, setSelectedRegionId] = useState<
		number | null
	>(defaultRegionId);
	const [tpValueState, setTpValue] = useState<number>(
		defaultPriceList?.tp_value ?? 1
	);

	const selectedPriceListId = selectedPriceListIdState ?? defaultPriceListId;
	const selectedRegionId = selectedRegionIdState ?? defaultRegionId;

	const selectedRegion = useMemo(() => {
		return (
			activatedRegions.find((r) => r.id === selectedRegionId) ??
			activatedRegions[0] ??
			null
		);
	}, [activatedRegions, selectedRegionId]);

	const taxRate = selectedRegion?.mwst_percentage ?? 8.1;

	const initialTemplateId =
		templateId && templatesLookup[Number(templateId)]
			? Number(templateId)
			: null;
	const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(
		initialTemplateId
	);

	const [hoveredTemplateId, setHoveredTemplateId] = useState<number | null>(
		null
	);

	const filteredTemplates = Object.values(templatesLookup)
		.filter(
			(template) =>
				template.name
					?.toLowerCase()
					.includes(searchQuery.toLowerCase()) ||
				template.code?.toLowerCase().includes(searchQuery.toLowerCase())
		)
		.sort((a, b) => a.name?.localeCompare(b.name ?? "") ?? 0);

	const handleRequestDeleteTemplate = (templateId: number) => {
		const folderNames = checkItemUsage({ template_id: templateId });
		setDeleteCatalogFolders(folderNames);
		setDeleteDialogTemplateId(templateId);
	};

	const handleConfirmDeleteTemplate = async () => {
		if (!deleteDialogTemplateId) return;
		setSelectedTemplateId(null);
		setDeleteDialogTemplateId(null);
		setDeleteCatalogFolders([]);
		await deleteTemplate(deleteDialogTemplateId);
		setSearchParams((prev) => {
			prev.delete("templateId");
			return prev;
		});
	};

	const handleCreateTemplate = async (
		code: string,
		name: string,
		jobItems?: JobItemEntityType[]
	) => {
		const existingTemplate = Object.values(templatesLookup).find(
			(t) => t.code === code
		);
		const newCode = existingTemplate?.code ? `${code}-2` : code;
		const result = await createTemplate(newCode, name, jobItems ?? []);
		if (result) {
			showNotification({
				message: "Jumbo/Vorlage erstellt",
				type: "success",
			});
			if (existingTemplate) {
				showNotification({
					message:
						"Der angegebene Code existiert bereits. Der Code wurde leicht abgeändert.",
					type: "warning",
				});
			}
			setSelectedTemplateId(result.id);
		}
	};

	const handleDuplicateTemplate = async () => {
		if (!selectedTemplateId) {
			showNotification({
				message: "Keine Vorlage ausgewählt",
				type: "error",
			});
			return;
		}
		const template = templatesLookup[selectedTemplateId];
		if (template) {
			const newCode = `${template.code}-2`;
			const newName = `${template.name} (Duplikat)`;
			const result = await createTemplate(
				newCode,
				newName,
				template.template_items
			);
			if (result) {
				showNotification({
					message: "Jumbo/Vorlage dupliziert",
					type: "success",
				});
				setSelectedTemplateId(result.id);
			}
		}
	};

	const handleCreateFromStandardTemplate = (templateCode: string) => {
		const template = standardJobDocumentTemplates.find(
			(t) => t.code === templateCode
		);
		if (template) {
			handleCreateTemplate(
				template.code,
				template.name,
				template.job_items as JobItemEntityType[]
			);
		}
	};

	return (
		<div className="flex flex-row w-full h-full overflow-hidden">
			<div className="p-6 flex flex-col w-full gap-4 h-[calc(100vh-100px)]">
				<div className="flex flex-row w-full gap-4 h-full">
					<Card
						sx={{
							height: "100%",
							minWidth: "300px",
							borderRadius: "0.5rem",
							boxShadow:
								"0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
							transition:
								"box-shadow 0.3s ease, transform 0.3s ease",
							"&:hover": {
								boxShadow:
									"0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
							},
						}}
						className="animate-in slide-in-from-left duration-300"
					>
						<div className="flex flex-row gap-2 m-2 w-full">
							<TextField
								size="small"
								placeholder="Suchen..."
								value={searchQuery}
								onChange={(e) => setSearchQuery(e.target.value)}
								sx={{
									"& .MuiInput-underline:before": {
										borderBottomColor: "rgba(0, 0, 0, 0.1)",
									},
									"& .MuiInput-underline:hover:not(.Mui-disabled):before":
										{
											borderBottomColor:
												"rgba(0, 0, 0, 0.3)",
										},
								}}
								fullWidth
								variant="standard"
							/>
						</div>
						<List
							sx={{
								overflow: "auto",
								width: "100%",
								maxHeight: "calc(100vh - 230px)",
								padding: "0.5rem",
							}}
						>
							{filteredTemplates.map((template) => (
								<ListItem
									key={template.id}
									disablePadding
									onMouseEnter={() =>
										setHoveredTemplateId(template.id)
									}
									onMouseLeave={() =>
										setHoveredTemplateId(null)
									}
								>
									<ListItemButton
										selected={
											selectedTemplateId === template.id
										}
										onClick={() => {
											setSelectedTemplateId(template.id);
											setSearchParams((prev) => {
												prev.set(
													"templateId",
													template.id.toString()
												);
												return prev;
											});
										}}
									>
										<div className="w-[calc(100%-30px)]">
											<ListItemText
												primary={displayTemplateLabel(
													template.code,
													template?.name ?? undefined
												)}
											/>
										</div>
										{(selectedTemplateId === template.id ||
											hoveredTemplateId ===
												template.id) && (
											<IconButton
												key={template.id}
												onClick={(e) => {
													e.stopPropagation();
													handleRequestDeleteTemplate(
														template.id
													);
												}}
												aria-label="Vorlage löschen"
												data-testid={`delete-template-${template.id}`}
											>
												<DeleteIcon
													sx={{ fontSize: 18 }}
												/>
											</IconButton>
										)}
									</ListItemButton>
								</ListItem>
							))}
						</List>
					</Card>
					{!selectedTemplateId ? (
						<div className="flex flex-col gap-4 h-full w-full">
							<div className="flex mb-4 animate-in slide-in-from-left duration-300">
								<Button
									variant="outline"
									className="mr-4 shadow-md hover:shadow-lg transition-all duration-300 bg-white hover:bg-gray-50 border border-gray-200 text-gray-800"
									onClick={() =>
										setCreateNewTemplateDialogOpen(true)
									}
								>
									<PlusSquare className="mr-2 h-4 w-4" />
									Neue Vorlage erstellen
								</Button>
								<Button
									variant="outline"
									className="shadow-md hover:shadow-lg transition-all duration-300 bg-white hover:bg-gray-50 border border-gray-200 text-gray-800"
									onClick={() => {
										setCreateFromStandardTemplateDialogOpen(
											true
										);
									}}
								>
									<Wand2 className="mr-2 h-4 w-4" />
									Mit Standardvorlage beginnen
								</Button>
							</div>
						</div>
					) : (
						<div className="flex flex-row gap-4 h-full animate-in fade-in-0 duration-300">
							<div className="flex flex-col h-full w-full animate-in slide-in-from-left duration-500">
								<JobDocumentTemplateCard
									templateId={selectedTemplateId}
								/>
							</div>
							{openedSidebar === null && (
								<div className="flex flex-col items-start gap-3 pt-4 animate-in slide-in-from-right duration-500">
									<Tooltip
										title="Neue Vorlage erstellen"
										placement="right"
									>
										<Button
											variant="outline"
											className="shadow-sm hover:shadow-md transition-all duration-300 bg-white hover:bg-gray-50 border border-gray-200 text-gray-800 p-3 gap-3"
											onClick={() =>
												setCreateNewTemplateDialogOpen(
													true
												)
											}
										>
											<PlusSquare className="h-5 w-5" />
											Neu
										</Button>
									</Tooltip>
									<Tooltip
										title="Mit Standardvorlage beginnen"
										placement="right"
									>
										<Button
											variant="outline"
											className="shadow-sm hover:shadow-md transition-all duration-300 bg-white hover:bg-gray-50 border border-gray-200 text-gray-800 p-3 gap-3"
											onClick={() => {
												setCreateFromStandardTemplateDialogOpen(
													true
												);
											}}
										>
											<Wand2 className="h-5 w-5" />
											Mit Standard
										</Button>
									</Tooltip>
									<Tooltip
										title="Diese Vorlage duplizieren"
										placement="right"
									>
										<Button
											variant="outline"
											className="shadow-sm hover:shadow-md transition-all duration-300 bg-white hover:bg-gray-50 border border-gray-200 text-gray-800 p-3 gap-3"
											onClick={handleDuplicateTemplate}
										>
											<Copy className="h-5 w-5" />
											Duplizieren
										</Button>
									</Tooltip>
									<div className="mt-4 w-full min-w-[200px]">
										<TemplateTotals
											templateItems={
												templatesLookup[
													selectedTemplateId
												]?.template_items ?? []
											}
											tariffsLookup={tariffsLookup}
											articlesLookup={articles}
											selectedPriceListId={
												selectedPriceListId
											}
											tpValue={tpValueState}
											taxRate={taxRate}
											roundTo={
												(selectedRegion?.round_to as RoundToType) ??
												RoundToType.TO_RAPPEN
											}
										/>
										<div className="mt-4 flex flex-row gap-2 items-end">
											<TextField
												label="TP"
												type="number"
												size="small"
												value={tpValueState}
												onChange={(e) =>
													setTpValue(
														parseFloat(
															e.target.value
														) || 0
													)
												}
												inputProps={{
													step: 0.01,
													min: 0,
												}}
												sx={{ minWidth: 85 }}
											/>
											<PriceListSelect
												value={selectedPriceListId}
												onChange={(value) => {
													setSelectedPriceListId(
														value
													);
													const newPriceList =
														priceLists.find(
															(pl) =>
																pl.id === value
														);
													if (
														newPriceList?.tp_value
													) {
														setTpValue(
															newPriceList.tp_value
														);
													}
												}}
												showEmpty={false}
												label=""
												width="150px"
											/>
											{activatedRegions.length > 1 && (
												<TextField
													select
													label="Region"
													size="small"
													value={
														selectedRegionId ?? ""
													}
													onChange={(e) =>
														setSelectedRegionId(
															Number(
																e.target.value
															)
														)
													}
													sx={{ minWidth: 120 }}
													SelectProps={{
														native: true,
													}}
												>
													{activatedRegions.map(
														(region) => (
															<option
																key={region.id}
																value={
																	region.id
																}
															>
																{REGION_METADATA[
																	region
																		.region
																]?.label ??
																	region.region}{" "}
																(
																{
																	region.mwst_percentage
																}
																%)
															</option>
														)
													)}
												</TextField>
											)}
										</div>
									</div>
								</div>
							)}
						</div>
					)}
					<div className="w-full h-full" ref={container}></div>
				</div>
			</div>
			<CreateNewTemplateDialog
				onCreateTemplate={handleCreateTemplate}
				open={createNewTemplateDialogOpen}
				setOpen={setCreateNewTemplateDialogOpen}
			/>
			<StandardTemplatesDialog
				open={createFromStandardTemplateDialogOpen}
				onClose={() => setCreateFromStandardTemplateDialogOpen(false)}
				templates={standardJobDocumentTemplates}
				onCreateFromStandardTemplate={handleCreateFromStandardTemplate}
			/>
			<AlertDialog
				open={deleteDialogTemplateId !== null}
				onOpenChange={(open) => {
					if (!open) {
						setDeleteDialogTemplateId(null);
						setDeleteCatalogFolders([]);
					}
				}}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Vorlage löschen</AlertDialogTitle>
						<AlertDialogDescription asChild>
							<div>
								<p className="mb-2">
									Möchten Sie die Vorlage &quot;
									{deleteDialogTemplateId
										? templatesLookup[
												deleteDialogTemplateId
											]?.name ||
											templatesLookup[
												deleteDialogTemplateId
											]?.code ||
											""
										: ""}
									&quot; wirklich löschen?
								</p>
								{deleteCatalogFolders.length > 0 && (
									<>
										<p className="mb-2">
											Diese Vorlage wird in folgenden
											Leistungskatalog-Ordnern verwendet
											und dort entfernt:
										</p>
										<ul className="list-disc list-inside mb-2 max-h-40 overflow-y-auto">
											{deleteCatalogFolders.map(
												(name, i) => (
													<li
														key={i}
														className="text-sm"
													>
														{name}
													</li>
												)
											)}
										</ul>
									</>
								)}
							</div>
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>Abbrechen</AlertDialogCancel>
						<AlertDialogAction
							onClick={handleConfirmDeleteTemplate}
							className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
							data-testid="confirm-delete-template"
						>
							Löschen
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	);
};

const JobDocumentTemplateCard: React.FC<{
	templateId: number;
}> = ({ templateId }) => {
	const {
		updateTemplateItemQuantity,
		deleteTemplateItem,
		updateTemplateName,
		updateTemplateCode,
		templatesLookup,
		addTemplateItem,
		pasteTemplateItems,
	} = useTemplatesStore((state) => ({
		deleteTemplate: state.deleteTemplate,
		updateTemplateItemQuantity: state.updateTemplateItemQuantity,
		deleteTemplateItem: state.deleteTemplateItem,
		updateTemplateName: state.updateTemplateName,
		updateTemplateCode: state.updateTemplateCode,
		templatesLookup: state.templatesLookup,
		addTemplateItem: state.addTemplateItem,
		pasteTemplateItems: state.pasteTemplateItems,
	}));
	const { articles } = useCentralStore((state) => ({
		articles: state.articles,
	}));
	const { tariffsLookup: tariffs } = useCatalogsStore((state) => ({
		tariffsLookup: state.tariffsLookup,
	}));

	const { clipboardData } = useClipboard();

	// For the big toolbar button: 2-minute time limit
	const hasRecentJobItemsInClipboard = useMemo(() => {
		if (!clipboardData.timestamp || clipboardData.data.length === 0) {
			return false;
		}
		if (!clipboardData.type.includes("job_items")) {
			return false;
		}
		const twoMinutesInMs = 2 * 60 * 1000;
		const isRecent = Date.now() - clipboardData.timestamp < twoMinutesInMs;
		return isRecent;
	}, [clipboardData]);

	const template = templatesLookup[templateId];
	const {
		name: templateName,
		code: templateCode,
		template_items: templateItems,
	} = template;

	const [title, setTitle] = useState(templateName);
	const [code, setCode] = useState(templateCode);

	useEffect(() => {
		setTitle(templateName);
		setCode(templateCode);
	}, [templateId]);

	useEffect(() => {
		if (template) {
			setTitle(template.name);
			setCode(template.code);
		}
	}, [template]);

	const handleTitleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setTitle(e.target.value);
	};

	const handleCodeInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setCode(e.target.value);
	};

	const handleTitleSubmit = async () => {
		if (title) await updateTemplateName(templateId, title);
	};

	const handleCodeSubmit = async () => {
		if (code) await updateTemplateCode(templateId, code);
	};

	const handleTitleReset = () => {
		setTitle(templateName);
	};

	const handleCodeReset = () => {
		setCode(templateCode);
	};

	const handlePasteRows = (
		pastedRows: any[],
		metaKeyPressed: boolean = false
	) => {
		pasteTemplateItems(pastedRows, templateId, metaKeyPressed);
	};

	const handleAddTariffByCode = (code_e: string, force?: boolean) => {
		const tariffsForCode = tariffs[code_e];
		if (tariffsForCode && Object.keys(tariffsForCode).length > 0) {
			const tariff = Object.values(tariffsForCode)[0];
			if (tariff.is_custom) {
				addTemplateItem(templateId, {
					code_e,
					tariffs_custom_id: tariff.id,
					quantity: 1,
				});
			} else {
				addTemplateItem(templateId, {
					code_e,
					tariffs_default_id: tariff.id,
					quantity: 1,
				});
			}
		} else {
			showNotification({
				message: `Tarif "${code_e}" nicht gefunden`,
				type: "error",
			});
		}
	};

	const handleAddArticleByCode = (code_e: string, force?: boolean) => {
		const articlesForCode = articles[code_e];
		if (articlesForCode && Object.keys(articlesForCode).length > 0) {
			const article = Object.values(articlesForCode)[0];
			addTemplateItem(templateId, {
				code_e,
				article_id: article.id,
				quantity: 1,
			});
		} else {
			showNotification({
				message: `Artikel "${code_e}" nicht gefunden`,
				type: "error",
			});
		}
	};

	const handleAddTariffById = (
		code_e: string,
		tariffId: string,
		options?: { force: boolean }
	) => {
		const tariffsForCode = tariffs[code_e];
		const tariff = tariffsForCode?.[tariffId];
		if (tariff?.is_custom) {
			addTemplateItem(templateId, {
				code_e,
				tariffs_custom_id: tariffId,
				quantity: 1,
			});
		} else {
			addTemplateItem(templateId, {
				code_e,
				tariffs_default_id: tariffId,
				quantity: 1,
			});
		}
	};

	const handleAddArticleById = (
		code_e: string,
		id: number,
		options?: { force: boolean }
	) => {
		addTemplateItem(templateId, { code_e, article_id: id, quantity: 1 });
	};

	const handleAddTemplate = (code_e: string) => {
		const template = Object.values(templatesLookup).find(
			(t) => t.code === code_e
		);
		if (template) {
			template.template_items.forEach((item) => {
				addTemplateItem(templateId, item);
			});
		} else {
			showNotification({
				message: "Vorlage nicht gefunden",
				type: "error",
			});
		}
	};

	const handleQuantityChange = async (itemId: number, quantity: number) => {
		await updateTemplateItemQuantity(templateId, itemId, quantity);
	};

	return (
		<Card
			elevation={2}
			sx={{
				borderRadius: "0.5rem",
				boxShadow:
					"0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
				transition: "box-shadow 0.3s ease",
				"&:hover": {
					boxShadow:
						"0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
				},
			}}
		>
			<CardContent>
				<div className="flex flex-row gap-2">
					<EditableTitle
						name="title"
						key={`title-${templateId}`}
						value={title ?? ""}
						onChange={handleTitleInputChange}
						onSubmit={handleTitleSubmit}
						onReset={handleTitleReset}
					/>
					<EditableTitle
						name="code"
						key={`code-${templateId}`}
						value={code ?? ""}
						onChange={handleCodeInputChange}
						onSubmit={handleCodeSubmit}
						onReset={handleCodeReset}
						type="code"
					/>
				</div>
				<div className="flex flex-row items-center gap-4 mt-4">
					<Toolbar
						onAddTariffByCode={handleAddTariffByCode}
						onAddArticleByCode={handleAddArticleByCode}
						onAddTariffById={handleAddTariffById}
						onAddArticleById={handleAddArticleById}
						onAddTemplate={handleAddTemplate}
						jobItemCodes={templateItems}
						jobDocumentId={0}
						jobDocumentType={"template"}
						itemSelectionDialog={null}
						handleItemSelection={() => {}}
						hasRecentJobItemsInClipboard={
							hasRecentJobItemsInClipboard
						}
						clipboardData={clipboardData.data}
						onPaste={handlePasteRows}
						canAddItems={true}
					/>
				</div>
				<Stack
					direction="row"
					spacing={1}
					sx={{ mt: 2 }}
					flexWrap="wrap"
					gap={1}
				>
					<TableContainer
						sx={{
							overflow: "auto",
							width: "100%",
							height: "calc(100vh - 300px)",
						}}
					>
						<Table size="small" stickyHeader>
							<TableHead>
								<TableRow>
									<TableCell>Eingabe</TableCell>
									<TableCell>Anzeige</TableCell>
									<TableCell>Art</TableCell>
									<TableCell>Bezeichnung</TableCell>
									<TableCell>Menge</TableCell>
									<TableCell align="right">
										Aktionen
									</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{[...templateItems]
									.sort((a, b) => {
										const aDetails = getTemplateItemDetails(
											a,
											tariffs,
											articles
										);
										const bDetails = getTemplateItemDetails(
											b,
											tariffs,
											articles
										);
										return aDetails.code_e.localeCompare(
											bDetails.code_e
										);
									})
									.map((item) => {
										const {
											code_e,
											code,
											type,
											description,
											found,
										} = getTemplateItemDetails(
											item,
											tariffs,
											articles
										);

										return (
											<TableRow key={item.id}>
												<TableCell>
													<Tooltip
														title={
															!found
																? "Tarif oder Artikel nicht gefunden"
																: ""
														}
													>
														<span
															className={
																!found
																	? "text-red-500"
																	: ""
															}
														>
															{code_e}
														</span>
													</Tooltip>
												</TableCell>
												<TableCell>
													<span
														className={
															!found
																? "text-red-500"
																: ""
														}
													>
														{code}
													</span>
												</TableCell>
												<TableCell>{type}</TableCell>
												<TableCell>
													{description}
												</TableCell>
												<TableCell>
													<TemplateItemQuantityInput
														quantity={item.quantity}
														onChange={(quantity) =>
															handleQuantityChange(
																item.id,
																quantity
															)
														}
													/>
												</TableCell>
												<TableCell align="right">
													<IconButton
														size="small"
														onClick={() =>
															deleteTemplateItem(
																templateId,
																item.id
															)
														}
													>
														<DeleteIcon />
													</IconButton>
												</TableCell>
											</TableRow>
										);
									})}
							</TableBody>
						</Table>
					</TableContainer>
				</Stack>
			</CardContent>
		</Card>
	);
};

const TemplateItemQuantityInput: React.FC<{
	quantity: number;
	onChange: (quantity: number) => void;
}> = ({ quantity, onChange }) => {
	const [value, setValue] = useState(quantity);
	useAutoSave({
		data: value,
		onSave: () => {
			if (value !== quantity && !isNaN(value)) {
				onChange(value);
			}
		},
	});

	useEffect(() => {
		setValue(quantity);
	}, [quantity]);

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const newValue = parseInt(e.target.value);
		const regex = /^[0-9]*$/;
		if (!regex.test(e.target.value)) {
			return;
		}
		setValue(newValue);
	};

	return (
		<TextField
			type="number"
			size="small"
			value={value}
			className="w-14"
			onChange={handleChange}
		/>
	);
};
